import { reactive, computed } from 'vue';
import type { RequestContent } from '../types';
import { EXPIRATION_OPTIONS, EXPIRATION_OPTION_HOURS } from '../constants';

interface FormData {
  name: string;
  message: string;
  customId: string;
  contentType: string;
  expiresOption: number | null;
  collectionId: string | null;
  collectLogs: boolean;
}

interface UseRequestContentModalFormOptions {
  edit: boolean;
  item?: RequestContent | null;
  domain: string;
  organizationSettings?: {
    defaultTitle?: string;
    defaultDescription?: string;
    contentTypeDefaults?: Record<string, { title?: string; description?: string }>;
  };
  organizationName?: string;
}

export const useRequestContentModalForm = (options: UseRequestContentModalFormOptions) => {
  const { edit, item, organizationSettings, organizationName } = options;

  // System default name per content type
  const getSystemDefaultName = (contentType: string = 'video') => {
    if (organizationName) {
      switch (contentType) {
        case 'file':
          return `${organizationName} needs your help with a file upload.`;
        case 'image':
          return `${organizationName} needs your help with a screenshot.`;
        default:
          return `${organizationName} needs your help with a short video.`;
      }
    }
    switch (contentType) {
      case 'file':
        return 'Upload a file.';
      case 'image':
        return 'Share a screenshot.';
      default:
        return 'Record a short video.';
    }
  };

  // System default message per content type
  const getSystemDefaultMessage = (contentType: string = 'video') => {
    switch (contentType) {
      case 'file':
        return '';
      case 'image':
        return '';
      default:
        return '';
    }
  };

  // Organization default title per content type (from jsonb column)
  const getOrgDefaultTitle = (contentType: string = 'video') => {
    const typeDefaults = organizationSettings?.contentTypeDefaults?.[contentType];
    if (typeDefaults?.title) {
      return typeDefaults.title;
    }
    // Fallback to legacy fields for video type
    if (contentType === 'video') {
      return organizationSettings?.defaultTitle;
    }
    return undefined;
  };

  // Organization default description per content type (from jsonb column)
  const getOrgDefaultDescription = (contentType: string = 'video') => {
    const typeDefaults = organizationSettings?.contentTypeDefaults?.[contentType];
    if (typeDefaults?.description) {
      return typeDefaults.description;
    }
    // Fallback to legacy fields for video type
    if (contentType === 'video') {
      return organizationSettings?.defaultDescription;
    }
    return undefined;
  };

  // Expiration options from constants
  const expirationOptions = EXPIRATION_OPTIONS;

  const calculateHoursFromExpiresAt = (expiresAt?: string): number | null => {
    if (!expiresAt) {
      return null;
    }

    const now = new Date();
    const expirationDate = new Date(expiresAt);
    const diffInMs = expirationDate.getTime() - now.getTime();

    if (diffInMs <= 0) {
      return null; // Already expired
    }

    const diffInHours = Math.ceil(diffInMs / (1000 * 60 * 60));

    // Find the closest expiration option
    const closest = EXPIRATION_OPTION_HOURS.reduce((prev, curr) => (Math.abs(curr - diffInHours) < Math.abs(prev - diffInHours) ? curr : prev));

    return closest;
  };

  // Note: computeExpirationDate removed as it was unused

  const createFormDataFromItem = (requestContentItem: RequestContent): FormData => ({
    name: requestContentItem.name,
    message: requestContentItem.message || '',
    customId: requestContentItem.customId || '',
    contentType: requestContentItem.contentType || 'video',
    expiresOption: calculateHoursFromExpiresAt(requestContentItem.expiresAt),
    collectionId: requestContentItem.collectionId || null,
    collectLogs: requestContentItem.collectLogs || false,
  });

  const createDefaultFormData = (): FormData => ({
    name: getOrgDefaultTitle('video') || getSystemDefaultName('video'),
    message: getOrgDefaultDescription('video') || getSystemDefaultMessage('video'),
    customId: '',
    contentType: 'video',
    expiresOption: null,
    collectionId: null,
    collectLogs: false,
  });

  const formData = reactive<FormData>(edit && item ? createFormDataFromItem(item) : createDefaultFormData());

  // Computed properties for better form management
  const submitButtonText = computed(() => `${edit ? 'Update' : 'Create'} Request Link`);

  // Custom validation for name - must have content or be completely empty (for defaults)
  const isNameValid = computed(() => {
    // If name is provided, it must not be only whitespace
    if (formData.name && formData.name.trim().length === 0) {
      return false; // Invalid: has characters but only whitespace
    }
    return true; // Valid: either has real content or is empty (defaults will be used)
  });

  // Note: Form dirty state is now handled by FormKit's state.dirty

  // Handle form submission with data cleaning
  const handleSubmit = (submittedData: any, getCollectionIdForSaving: () => string | null, logCollectionDomain?: string) => {
    const cleanData = { ...submittedData };
    const currentType = cleanData.contentType || 'video';
    // Use organization defaults when name or message are empty
    if (!cleanData.name?.trim()) {
      cleanData.name = getOrgDefaultTitle(currentType) || getSystemDefaultName(currentType);
    }

    if (!cleanData.message?.trim()) {
      cleanData.message = getOrgDefaultDescription(currentType) || getSystemDefaultMessage(currentType);
    }

    // Remove customId if empty
    if (!cleanData.customId?.trim()) {
      delete cleanData.customId;
    }

    // Convert expiresOption to expiresAt timestamp or set to null if "Never" selected
    const expiresOption = Number(cleanData.expiresOption);
    if (Number.isFinite(expiresOption) && expiresOption > 0) {
      cleanData.expiresAt = new Date(Date.now() + expiresOption * 60 * 60 * 1000).toISOString();
    } else {
      // Explicitly set to null when "Never" is selected to clear any existing expiration
      cleanData.expiresAt = null;
    }

    delete cleanData.expiresOption;

    // Set collectionId from composable
    const collectionId = getCollectionIdForSaving();
    if (collectionId) {
      cleanData.collectionId = collectionId;
    } else {
      delete cleanData.collectionId;
    }

    // Clear collectLogs for non-video content types
    if (cleanData.contentType && cleanData.contentType !== 'video') {
      cleanData.collectLogs = false;
    }

    // Set logCollectionDomain if collectLogs is enabled
    if (cleanData.collectLogs && logCollectionDomain) {
      cleanData.logCollectionDomain = logCollectionDomain;
    }

    // Remove UI-only field
    delete cleanData.collectionSelection;

    return cleanData;
  };

  return {
    formData,
    expirationOptions,
    submitButtonText,
    isNameValid,
    handleSubmit,
    getSystemDefaultName,
    getSystemDefaultMessage,
    getOrgDefaultTitle,
    getOrgDefaultDescription,
  };
};
